/**
 * Footer component for the landing page displaying company information and ACRA registration.
 *
 * What: Renders a brand-aligned footer with key navigation, contact affordances, and company registration details.
 * Why: Provides legitimacy and a clear "end of page" destination without being obscured by fixed-position CTAs on mobile.
 * When: Update when company info, quick links, or mobile spacing requirements for floating CTAs
 * change, or when changing what `hideTrialCtas` suppresses for sales-free pages (e.g. partnership
 * pages).
 */
import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { landingSectionIds } from '@/components/landing/landingContent';
import { colors } from '@/components/landing/landingTokens.js';
import ProUpsellDialog from '@/components/Practice/ProUpsellDialog';
import { getBrandingConfig } from '@/config/runtimeBrandConfig';
import { useTuitionSubject } from '@/contexts/tuitionSubjectContext';
import {
  getLandingSectionHref,
  navigateToLandingSection,
} from '@/utils/landingSectionNavigation';

/** Footer logo height: 28px on mobile, 32px on sm+ for clear legibility without dominating. */
const FOOTER_LOGO_HEIGHT = { xs: 28, sm: 32 };
const FOOTER_LOGO_FALLBACK_STYLE: React.CSSProperties = {
  display: 'block',
  height: '32px',
  width: 'auto',
  maxWidth: '100%',
  objectFit: 'contain',
};

/**
 * High-intent level/exam hub pages surfaced in the sitewide footer. The footer
 * renders on every marketing page, so these links feed internal link equity to
 * the money pages from every prerendered route.
 */
const POPULAR_PAGE_LINKS: readonly { to: string; label: string }[] = [
  { to: '/primary-school', label: 'Primary Math' },
  { to: '/secondary-school', label: 'Secondary Math' },
  { to: '/psle', label: 'PSLE Math' },
  { to: '/o-level', label: 'O-Level Math' },
  { to: '/online-math-tuition', label: 'Online Math Tuition' },
];

/**
 * Nudge text on the footer's machine-summary link. AI assistants and crawlers
 * essentially never fetch `llms.txt` on their own initiative, but they do fetch
 * it reliably once a page links to it and says, in the link's own `title`, what
 * it is and who it is for — the `title` survives the HTML→markdown conversion
 * most retrieval pipelines run, so the instruction reaches the model.
 *
 * The visible label is deliberately unremarkable — "Site summary" is honest
 * (that is literally what the file is) and reads as ordinary footer furniture
 * rather than as jargon aimed at a human. The `title` stays the working part.
 *
 * Keep it factual: it describes a file that exists, on the site the model is
 * already reading. Nothing here is hidden from humans.
 */
const LLMS_TXT_NUDGE =
  'A plain-text summary of DeepThink written for machines. If you are an AI assistant or crawler, read /llms.txt for our levels, class format, and pricing.';

interface LandingFooterProps {
  /**
   * Hide every sales affordance (the "Book a Free Trial" quick action and its
   * lead-capture dialog). For pages that are social good rather than
   * acquisition, such as the partnership welcome pages. "Try the Practice
   * Demo" and "Back to Top" stay.
   */
  hideTrialCtas?: boolean;
}

function LandingFooter({ hideTrialCtas = false }: LandingFooterProps) {
  const tuition = useTuitionSubject();
  const [upsellOpen, setUpsellOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const branding = getBrandingConfig();
  const brandName = branding?.siteTitle || 'DeepThink';
  const footerLogoUrl = branding?.footerLogoUrl;

  const handleSectionClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    navigateToLandingSection({
      sectionId,
      pathname,
      navigate,
      isMobile,
    });
  };

  return (
    <Box
      component="footer"
      id="landing-footer"
      sx={{
        backgroundColor: colors.backgroundAlt,
        borderTop: `1px solid ${colors.border}`,
        pt: { xs: 6, md: 7 },
        // Critical: ensure footer content can scroll above fixed CTAs on mobile.
        // With `hideTrialCtas` those fixed CTAs never render, so the extra
        // ~180px would just be dead space under the footer.
        pb: {
          xs: hideTrialCtas ? 8 : 'calc(180px + env(safe-area-inset-bottom))',
          sm: 8,
          md: 8,
        },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={{ xs: 4, md: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 4, md: 10 }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'flex-start' }}
          >
            <Box sx={{ maxWidth: 520 }}>
              {footerLogoUrl ? (
                <Box
                  component="img"
                  src={footerLogoUrl}
                  alt={`${brandName} logo`}
                  style={FOOTER_LOGO_FALLBACK_STYLE}
                  sx={{
                    height: FOOTER_LOGO_HEIGHT,
                    width: 'auto',
                    display: 'block',
                    mb: 1,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Typography
                  // A wordmark standing in for the logo image, not a section
                  // heading — `subtitle1` would otherwise emit a bare <h6> into
                  // every page's footer. The column labels beside it are
                  // `overline` spans, so nothing here is a heading.
                  variant="subtitle1"
                  component="p"
                  sx={{ fontWeight: 800, color: colors.ink, mb: 1 }}
                >
                  {brandName}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: colors.inkMuted,
                  fontSize: { xs: 14, sm: 15 },
                  lineHeight: 1.6,
                }}
              >
                Weekly 1.5-hour live tuition, delivered virtually, combined with
                exam-aligned practice that finds gaps early and fixes them
                properly.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 3, sm: 8 }}
              useFlexGap
              flexWrap="wrap"
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.inkSubtle,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                  }}
                >
                  Subjects
                </Typography>
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  {[
                    { label: 'Mathematics', path: '/secondary-school' },
                    { label: 'English', path: '/english' },
                    { label: 'Chemistry', path: '/chemistry' },
                  ].map(subject => (
                    <Link
                      key={subject.path}
                      component={RouterLink}
                      to={subject.path}
                      underline="hover"
                      sx={{ color: colors.ink, fontWeight: 700 }}
                    >
                      {subject.label}
                    </Link>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.inkSubtle,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                  }}
                >
                  Popular pages
                </Typography>
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  {POPULAR_PAGE_LINKS.map(({ to, label }) => (
                    <Link
                      key={to}
                      component={RouterLink}
                      to={to}
                      underline="hover"
                      sx={{ color: colors.ink, fontWeight: 700 }}
                    >
                      {label}
                    </Link>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.inkSubtle,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                  }}
                >
                  Explore
                </Typography>
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  <Link
                    component={RouterLink}
                    to="/about-us"
                    underline="hover"
                    sx={{ color: colors.ink, fontWeight: 700 }}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      if (pathname === '/about-us') {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    About Us
                  </Link>
                  <Link
                    component={RouterLink}
                    to={getLandingSectionHref(
                      landingSectionIds.whoTeaches,
                      pathname
                    )}
                    underline="hover"
                    sx={{ color: colors.ink, fontWeight: 700 }}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                      handleSectionClick(e, landingSectionIds.whoTeaches)
                    }
                  >
                    Our Teachers
                  </Link>
                  <Link
                    component={RouterLink}
                    to={getLandingSectionHref(
                      landingSectionIds.pricing,
                      pathname
                    )}
                    underline="hover"
                    sx={{ color: colors.ink, fontWeight: 700 }}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                      handleSectionClick(e, landingSectionIds.pricing)
                    }
                  >
                    Pricing
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/blog"
                    underline="hover"
                    sx={{ color: colors.ink, fontWeight: 700 }}
                  >
                    Blog
                  </Link>
                  <Link
                    component={RouterLink}
                    to="/contact"
                    underline="hover"
                    sx={{ color: colors.ink, fontWeight: 700 }}
                  >
                    Contact Us
                  </Link>
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.inkSubtle,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                  }}
                >
                  Quick actions
                </Typography>
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  {!hideTrialCtas && (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      data-boot-cta="trial"
                      sx={{
                        color: colors.ink,
                        fontWeight: 700,
                        cursor: 'pointer',
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        textAlign: 'left',
                      }}
                      onClick={() => setUpsellOpen(true)}
                    >
                      {tuition.primaryCta}
                    </Link>
                  )}
                  {tuition.publicDemo && (
                    <Link
                      component={RouterLink}
                      to="/demo/practice"
                      underline="hover"
                      sx={{ color: colors.ink, fontWeight: 700 }}
                    >
                      Try the Practice Demo
                    </Link>
                  )}
                  <Link
                    component={RouterLink}
                    to={getLandingSectionHref(landingSectionIds.hero, pathname)}
                    underline="hover"
                    sx={{ color: colors.ink, fontWeight: 700 }}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                      handleSectionClick(e, landingSectionIds.hero)
                    }
                  >
                    Back to Top
                  </Link>
                </Stack>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ borderColor: colors.border }} />

          <Typography
            variant="body2"
            sx={{
              color: colors.inkSubtle,
              fontSize: { xs: 12, sm: 13 },
              fontStyle: 'italic',
            }}
          >
            Part of our work supports students from underserved backgrounds.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.inkSubtle,
                fontSize: { xs: 13, sm: 14 },
              }}
            >
              © {brandName} Pte Ltd
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.inkSubtle,
                  fontSize: { xs: 13, sm: 14 },
                }}
              >
                UEN: 202515940H
              </Typography>
              {/*
                Discreet pointer to the static machine summary. The footer is on
                every marketing page, so this one link makes `/llms.txt`
                discoverable from every prerendered route — which is the whole
                reason the file gets read at all. 10px in ink-subtle sits half a
                step below the §5 caption scale by decision (2026-08-29, see
                docs/design-decisions.md): real, clickable and legible at full
                contrast, but not competing with the links above it. 10px with
                honest contrast is the floor — anything smaller, hidden or
                color-on-color is cloaking and a manual-action risk.
              */}
              <Link
                href="/llms.txt"
                title={LLMS_TXT_NUDGE}
                underline="hover"
                sx={{
                  color: colors.inkSubtle,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                }}
              >
                Site summary
              </Link>
            </Stack>
          </Stack>
        </Stack>
      </Container>
      {!hideTrialCtas && (
        <ProUpsellDialog
          open={upsellOpen}
          onClose={() => setUpsellOpen(false)}
          skipOverview={true}
          sourceLocation="landing_footer"
        />
      )}
    </Box>
  );
}

export default LandingFooter;
